import React, { useState, useEffect, useCallback } from 'react';
import { GuestbookEntry } from '../../../Types/guestbook';
import GuestbookAPI from '../../../services/api/GuestbookService';
import Table from '../../../UI/Table/Table';
import Modal from '../../../UI/Modal/Modal';
import Button from '../../../UI/Button/Button';
import { PaginationType } from '../../../Types/APITypes';

interface GuestbookAllEntriesProps {
  onEntryAction: () => void;
}

const GuestbookAllEntries: React.FC<GuestbookAllEntriesProps> = ({ onEntryAction }) => {
  const [entries, setEntries] = useState<GuestbookEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationType>({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
    has_more_pages: false
  });
  const [selectedEntry, setSelectedEntry] = useState<GuestbookEntry | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<{name: string, message: string} | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState<'all' | 'approved' | 'pending'>('all');

  const fetchEntries = useCallback(async (paginationData: PaginationType) => {
    try {
      setLoading(true);
      const response: any = await GuestbookAPI.get_all_entries(paginationData);
      if (response && response.data) {
        let filteredEntries = response.data.data || [];
        
        // Filtrer par statut si nécessaire
        if (filterStatus !== 'all') {
          filteredEntries = filteredEntries.filter((entry: GuestbookEntry) => {
            if (filterStatus === 'approved') return entry.is_approved;
            if (filterStatus === 'pending') return !entry.is_approved;
            return true;
          });
        }
        
        setEntries(filteredEntries);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0,
          last_page: response.data.pagination?.last_page || 1,
          has_more_pages: response.data.pagination?.has_more_pages || false
        }));
        setError(null);
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des témoignages');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetchEntries(pagination);
  }, [fetchEntries]);

  const handleApprove = async (entry: GuestbookEntry) => {
    try {
      setActionLoading(true);
      await GuestbookAPI.approve_entry(entry.id);
      onEntryAction();
      fetchEntries(pagination);
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'approbation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (entry: GuestbookEntry) => {
    try {
      setActionLoading(true);
      await GuestbookAPI.reject_entry(entry.id);
      onEntryAction();
      fetchEntries(pagination);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du rejet');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (entry: GuestbookEntry) => {
    try {
      setActionLoading(true);
      await GuestbookAPI.delete_entry(entry.id);
      onEntryAction();
      fetchEntries(pagination);
      setShowModal(false);
      setSelectedEntry(null);
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la suppression');
    } finally {
      setActionLoading(false);
    }
  };

  const openDeleteModal = (entry: GuestbookEntry) => {
    setSelectedEntry(entry);
    setShowModal(true);
  };

  const openMessageModal = (entry: GuestbookEntry) => {
    setSelectedMessage({
      name: entry.name,
      message: entry.message
    });
    setShowMessageModal(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            className={`h-4 w-4 ${
              star <= rating ? 'text-yellow-400' : 'text-gray-300'
            }`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
        <span className="ml-1 text-sm text-gray-600">({rating}/5)</span>
      </div>
    );
  };

  const renderStatus = (isApproved: boolean) => {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        isApproved 
          ? 'bg-green-100 text-green-800' 
          : 'bg-yellow-100 text-yellow-800'
      }`}>
        {isApproved ? 'Approuvé' : 'En attente'}
      </span>
    );
  };

  const columns: any[] = [
    {
      Header: 'Nom',
      accessor: 'name',
      Cell: ({ value }: { value: string }) => (
        <div className="font-medium text-gray-900">{value}</div>
      )
    },
    {
      Header: 'Email',
      accessor: 'email',
      Cell: ({ value }: { value: string }) => (
        <div className="text-gray-600">{value}</div>
      )
    },
    {
      Header: 'Message',
      accessor: 'message',
      Cell: ({ value, row }: { value: string; row: any }) => (
        <div className="flex items-center space-x-2">
          <div className="text-gray-600 truncate max-w-xs" title={value}>
            {value.length > 30 ? value.substring(0, 30) + '...' : value}
          </div>
          {value.length > 30 && (
            <button
              onClick={() => openMessageModal(row.original)}
              className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              title="Voir le message complet"
            >
              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </button>
          )}
        </div>
      )
    },
    {
      Header: 'Note',
      accessor: 'rating',
      Cell: ({ value }: { value: number }) => renderStars(value)
    },
    {
      Header: 'Statut',
      accessor: 'is_approved',
      Cell: ({ value }: { value: boolean }) => renderStatus(value)
    },
    {
      Header: 'Date',
      accessor: 'created_at',
      Cell: ({ value }: { value: string }) => (
        <div className="text-gray-600 text-sm">{formatDate(value)}</div>
      )
    },
    {
      Header: 'Actions',
      accessor: 'id',
      Cell: ({ row }: { row: any }) => (
        <div className="flex space-x-2">
          {!row.original.is_approved && (
            <button
              onClick={() => handleApprove(row.original)}
              disabled={actionLoading}
              className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
            >
              <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Approuver
            </button>
          )}
          {!row.original.is_approved && (
            <button
              onClick={() => handleReject(row.original)}
              disabled={actionLoading}
              className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-yellow-700 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50"
            >
              <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Rejeter
            </button>
          )}
          <button
            onClick={() => openDeleteModal(row.original)}
            disabled={actionLoading}
            className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
          >
            <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Supprimer
          </button>
        </div>
      )
    }
  ];

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Erreur</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Tous les témoignages
          </h2>
          
          {/* Filtres */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Filtrer par :</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as 'all' | 'approved' | 'pending')}
                className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">Tous</option>
                <option value="approved">Approuvés</option>
                <option value="pending">En attente</option>
              </select>
            </div>
          </div>
        </div>

        <Table
          columns={columns}
          data={entries}
          fetchData={fetchEntries}
          loading={loading}
          pageCount={pagination.last_page || 1}
          total={pagination.total || 0}
        />
      </div>

      {/* Modal de confirmation de suppression */}
      <Modal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedEntry(null);
        }}
        title="Confirmer la suppression"
      >
        <div className="p-6">
          <p className="text-gray-700 mb-4">
            Êtes-vous sûr de vouloir supprimer définitivement le témoignage de{' '}
            <strong>{selectedEntry?.name}</strong> ?
          </p>
          <p className="text-sm text-gray-600 mb-6">
            Cette action est irréversible et supprimera définitivement le témoignage de la base de données.
          </p>
          <div className="flex justify-end space-x-3">
            <Button
              color="info"
              onClick={() => {
                setShowModal(false);
                setSelectedEntry(null);
              }}
              disabled={actionLoading}
            >
              Annuler
            </Button>
            <Button
              color="error"
              onClick={() => selectedEntry && handleDelete(selectedEntry)}
              disabled={actionLoading}
            >
              Supprimer
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal d'affichage du message complet */}
      <Modal
        open={showMessageModal}
        onClose={() => {
          setShowMessageModal(false);
          setSelectedMessage(null);
        }}
        title={`Message de ${selectedMessage?.name}`}
      >
        <div className="p-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">
              {selectedMessage?.message}
            </p>
          </div>
          <div className="flex justify-end mt-6">
            <Button
              color="info"
              onClick={() => {
                setShowMessageModal(false);
                setSelectedMessage(null);
              }}
            >
              Fermer
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default GuestbookAllEntries; 